<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { CERTIFICATES_ERRORS } from "@/package/const/certificates-errors";
import MainInput from "@/components/helpers/MainInput.vue";
import MainButton from "@/components/helpers/MainButton.vue";
import MainButtonIcon from "@/package/components/MainButtonIcon.vue";

export default {
  name: "TariffBuyCertificates",

  components: { MainButtonIcon, MainButton, MainInput },

  emits: ["checkedCert", "isCertError", "setCerts"],

  props: {
    tariffId: {
      type: Number,
      required: true,
    },

    isAddDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      certificates: [
        {
          id: Date.now(),
          number: "",
          code: "",
          error: "",
          checked: false,
        },
      ],
    };
  },

  watch: {
    formattedCertificates: {
      handler(newValue) {
        this.$emit("setCerts", newValue);
      },
      deep: true,
    },
  },

  computed: {
    ...mapState({
      previewTariffPrice: (state) => state.tariff.previewTariffPrice,
    }),

    formattedCertificates() {
      let certificates = this.certificates.map((item) => item);

      certificates = certificates.filter(
        (certificate) =>
          certificate.number.length > 0 && certificate.code.length > 0
      );

      certificates = certificates.map((certificate) => {
        return {
          number: certificate.number,
          code: certificate.code,
        };
      });

      return certificates;
    },

    isShowDeleteButton() {
      return (
        this.certificates.length > 1 ||
        !!this.certificates.find((item) => item.checked)
      );
    },

    isAddCertDisabled() {
      return (
        this.isAddDisabled ||
        !!this.certificates.find((certificate) => !certificate.checked)
      );
    },
  },

  methods: {
    ...mapActions({
      loadPreviewTariffPrice: "tariff/loadPreviewTariffPrice",
    }),

    ...mapMutations({
      SET_PREVIEW_TARIFF_PRICE: "tariff/SET_PREVIEW_TARIFF_PRICE",
    }),

    addCertificate() {
      this.certificates.push({
        id: Date.now(),
        number: "",
        code: "",
        error: "",
        checked: false,
      });
    },

    deleteCertificate(id) {
      if (this.certificates.length === 1) {
        this.certificates = [];
        this.certificates.push({
          id: Date.now(),
          number: "",
          code: "",
          error: "",
          checked: false,
        });

        this.checkCertificate();

        return;
      }

      const index = this.certificates.findIndex(
        (certificate) => certificate.id === id
      );

      this.certificates.splice(index, 1);
      this.checkCertificate();
    },

    async checkCertificate() {
      try {
        this.isLoading = true;

        const response = await this.loadPreviewTariffPrice({
          tariffId: this.tariffId,
          certificates: this.formattedCertificates,
        });

        if (response.data.certificates === null) {
          localStorage.removeItem("certificates");
          this.$emit("checkedCert", false);
        }

        this.certificates?.forEach((item) => {
          item.error = "";
          item.checked = false;
        });

        response?.data?.certificates?.forEach((itemResponse) => {
          const certificate = this.certificates.find((item) => {
            return item.number === itemResponse.number;
          });

          if (certificate) {
            setTimeout(() => {
              certificate.checked = true;

              const validCerts = this.certificates.filter(
                (cert) => cert.checked
              );

              this.$emit("checkedCert", true);

              if (validCerts.length) {
                localStorage.setItem(
                  "certificates",
                  JSON.stringify(validCerts)
                );
              }
            });
          }
        });

        this.$emit("isCertError", false);
      } catch (error) {
        error.response?.data?.errors?.forEach((item) => {
          this.certificates[item.key].error =
            CERTIFICATES_ERRORS[item.message] ?? "Неизвестная ошибка";
          this.certificates[item.key].checked = false;

          const validCerts = this.certificates.filter((cert) => cert.checked);
          localStorage.setItem("certificates", JSON.stringify(validCerts));

          if (validCerts.length === 0) {
            this.SET_PREVIEW_TARIFF_PRICE(null);
          }
        });

        setTimeout(() => {
          const isValid = !!this.certificates.find((item) => item.checked);

          this.$emit("isCertError", true);
          this.$emit("checkedCert", isValid);
        });
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        });
      }
    },
  },

  mounted() {
    const certificates = localStorage.getItem("certificates");
    const parsedCertificates = JSON.parse(certificates);

    if (
      Array.isArray(parsedCertificates) &&
      parsedCertificates.length > 0 &&
      this.$route.params?.tariffId
    ) {
      this.certificates = JSON.parse(localStorage.getItem("certificates"));

      this.checkCertificate();
    }
  },
};
</script>

<template>
  <div class="tariff-buy-certificates">
    <h2>Сертификаты</h2>

    <p>
      Если стоимость тарифа меньше вашего сертификата, остаток суммы на
      сертификате сгорает
    </p>

    <span class="divider" />

    <div class="tariff-buy-certificates__list">
      <div
        v-for="certificate in certificates"
        :key="certificate.id"
        class="tariff-buy-certificates__item item"
        :class="{
          item_removable:
            isShowDeleteButton ||
            certificate.code.length > 0 ||
            certificate.number.length > 0,
        }"
      >
        <div class="item__inputs">
          <MainInput
            v-model="certificate.number"
            :label="'Номер сертификата'"
            :readonly="certificate.checked"
            :error="certificate.error.length > 0"
            @focusout="checkCertificate"
          >
            <template v-if="certificate.checked" #icon>
              <span class="icon-success" />
            </template>
          </MainInput>

          <MainInput
            v-model="certificate.code"
            :label="'Пин-код'"
            :readonly="certificate.checked"
            :mask="'####'"
            :error="certificate.error.length > 0"
            @focusout="checkCertificate"
          />
        </div>

        <MainButtonIcon
          v-if="
            isShowDeleteButton ||
            certificate.code.length > 0 ||
            certificate.number.length > 0
          "
          :disabled="isLoading"
          class="item__button-delete"
          @click="deleteCertificate(certificate.id)"
        >
          <span class="icon-close-bold" />
        </MainButtonIcon>

        <p v-if="certificate.error" class="item__error">
          {{ certificate.error }}
        </p>
      </div>
    </div>

    <MainButton
      class="tariff-buy-certificates__button-add"
      :title="'Добавить сертификат'"
      :disabled="isAddCertDisabled"
      :color="'blue'"
      @click="addCertificate"
    />
  </div>
</template>

<style lang="scss" scoped>
.tariff-buy-certificates {
  > h2 {
    @include body-1-bold;
    color: $dark-primary;
    margin-bottom: 8px;
  }

  > p {
    @include text-2;
    color: $dark-fifth;
  }

  .divider {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .item {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 16px;

    &_removable {
      grid-template-columns: 1fr 24px;
    }

    &__inputs {
      width: 560px;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .main-input {
      margin-bottom: 0;
      width: 100%;
    }

    &__error {
      @include caption-1;
      color: $red;
      padding: 0 4px;
      margin-top: 8px;
    }

    &__button-delete {
      margin-top: 40px;
      height: 24px;
      width: 24px;
    }

    .icon-close-bold {
      width: 24px;
      height: 24px;
      background-color: $dark-sixth;
    }

    .icon-success {
      position: absolute;
      top: 42px;
      right: 16px;
      width: 20px;
      height: 20px;
      background-color: $money-cv;
      z-index: 999;
    }
  }

  &__button-add {
    margin-top: 24px;

    @media (max-width: 900px) {
      margin-top: 16px;
    }
  }
}

@media (max-width: 1024px) {
  .tariff-buy-certificates {
    .item {
      &__button-delete {
        margin-top: 36px;
      }
    }
  }
}

@media (max-width: 576px) {
  .tariff-buy-certificates {
    .item {
      &__inputs {
        width: 100%;
        grid-template-columns: 1fr;
      }

      &__button-delete {
        margin-top: 34px;
      }
    }
  }
}
</style>
